import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Input, Modal, Row, Typography } from 'antd';
import { useState } from 'react';
import styled from 'styled-components';

import { useReasonOptions, useRefund } from '../../hooks/useCustomerFinancial';
import { Billing } from '../../types/customer';
import { SelectReason } from './components/SelectReason';

const { Text } = Typography;

type RefundModalProps = {
  open: boolean;
  onClose: () => void;
  billing?: Billing;
  customerDocument: string;
};

export const RefundModal: React.FC<RefundModalProps> = ({
  onClose,
  open,
  billing,
  customerDocument,
}) => {
  const { refund } = useRefund();
  const [partialRefundModalOpen, setPartialRefundModalOpen] = useState(false);
  const [totalRefundModalReason, setTotalRefundModalReason] = useState(false);
  const [refundAmount, setRefundAmount] = useState(0.0);
  const [selectedReason, setSelectedReason] = useState<number>(0);

  const { data: options } = useReasonOptions();
  const handleRefund = () => {
    setPartialRefundModalOpen(true);
  };

  const confirmPartialRefund = () => {
    if (!billing || !refundAmount) return;
    refund({
      gatewayTransaction: billing.lastPspReference,
      amount: refundAmount,
      billingId: billing.id,
      cancellation: false,
      document: customerDocument,
      reasonId: selectedReason,
    });
    closeAllModals();
  };

  const handleTotalRefund = () => {
    setTotalRefundModalReason(true);
  };
  const confirmRefundAndCancel = () => {
    if (!billing) return;
    refund({
      gatewayTransaction: billing.lastPspReference,
      amount: billing.amount,
      billingId: billing.id,
      cancellation: true,
      document: customerDocument,
      reasonId: selectedReason,
    });
    closeAllModals();
  };

  const closeAllModals = () => {
    onClose();
    setPartialRefundModalOpen(false);
  };

  const handleChange = (value: number) => {
    setSelectedReason(value);
  };

  return (
    <>
      <CustomModal
        open={open}
        title={
          <Row
            align={'middle'}
            gutter={24}
            style={{ gap: '12px', marginLeft: '0' }}
          >
            <ExclamationCircleOutlined
              style={{ color: '#FAAD14', fontSize: '22px' }}
            />
            <Text style={{ fontWeight: 600 }}>Reembolso</Text>
          </Row>
        }
        centered
        closable={false}
        width={650}
        footer={[
          <Button key='cancel' onClick={onClose}>
            Cancelar
          </Button>,
          <Button key='refund' type='primary' onClick={handleRefund}>
            Reembolso parcial
          </Button>,
          <Button
            key='refund-and-cancel'
            type='primary'
            danger
            onClick={handleTotalRefund}
          >
            Reembolsar total e cancelamento
          </Button>,
        ]}
      >
        <Text style={{ marginLeft: '18px', marginTop: '8px' }}>
          Você deseja cancelar a assinatura ou apenas receber o reembolso?
        </Text>
      </CustomModal>

      <CustomModal
        open={partialRefundModalOpen}
        title={
          <Row
            align={'middle'}
            gutter={24}
            style={{ gap: '12px', marginLeft: '0' }}
          >
            <ExclamationCircleOutlined
              style={{ color: '#FAAD14', fontSize: '22px' }}
            />
            <Text style={{ fontWeight: 600 }}>
              Digite o valor do reembolso parcial
            </Text>
          </Row>
        }
        centered
        closable={false}
        width={704}
        footer={[
          <Button key='cancel' onClick={() => setPartialRefundModalOpen(false)}>
            Cancelar
          </Button>,
          <Button key='confirm' type='primary' onClick={confirmPartialRefund}>
            Confirmar
          </Button>,
        ]}
      >
        <SelectReason
          description='Tem certeza que deseja reembolsar o usuário parcialmente? Selecione um motivo para continuar:'
          confirmationAsk='Esta alteração não poderá ser desfeita. Tem certeza que deseja prosseguir com a alteração? Caso sim, digite o valor do reembolso parcial abaixo:'
          options={options}
          handleChange={handleChange}
        />
        <Input
          type='number'
          placeholder='Valor do reembolso'
          value={refundAmount}
          onChange={e => setRefundAmount(parseFloat(e.target.value))}
          style={{ margin: '16px 0', width: '100%' }}
        />
      </CustomModal>

      <CustomModal
        open={totalRefundModalReason}
        title={
          <Row
            align={'middle'}
            gutter={24}
            style={{ gap: '12px', marginLeft: '0' }}
          >
            <ExclamationCircleOutlined
              style={{ color: '#FAAD14', fontSize: '22px' }}
            />
            <Text style={{ fontWeight: 600 }}>
              Reembolso total e cancelamento
            </Text>
          </Row>
        }
        centered
        closable={false}
        width={704}
        footer={[
          <Button key='cancel' onClick={() => setTotalRefundModalReason(false)}>
            Cancelar
          </Button>,
          <Button key='confirm' type='primary' onClick={confirmRefundAndCancel}>
            Confirmar
          </Button>,
        ]}
      >
        <SelectReason
          description='Tem certeza que deseja reembolsar o usuário totalmente e cancelar a assinatura? Caso sim, selecione um motivo para continuar:'
          confirmationAsk='Esta alteração não poderá ser desfeita. Tem certeza que deseja prosseguir com a alteração?'
          options={options}
          handleChange={handleChange}
        />
      </CustomModal>
    </>
  );
};

const CustomModal = styled(Modal)`
  & .ant-modal-header {
    padding: 32px 32px 0 32px;
    border-bottom: 0 !important;
  }

  & .ant-modal-body {
    padding-top: 0;
    padding: 16px 32px 16px 32px;
  }

  & .ant-modal-footer {
    border-top: 0 !important;
    padding: 0 32px 32px 32px;
  }
`;
